import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type AlertedTradeSource = 'insider' | 'congress';

interface AlertedTradesContextValue {
  wasAlerted: (tradeId: string, source: AlertedTradeSource) => boolean;
  count: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AlertedTradesContext = createContext<AlertedTradesContextValue | null>(null);

export const AlertedTradesProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [keys, setKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  const fetchAlerted = useCallback(async () => {
    if (!user) {
      setKeys(new Set());
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('alert_queue')
        .select('trade_id, trade_source')
        .eq('user_id', user.id)
        .not('sent_at', 'is', null);
      if (error) throw error;
      const set = new Set<string>();
      (data || []).forEach((r: any) => {
        if (r.trade_id && r.trade_source) {
          set.add(`${r.trade_source}:${r.trade_id}`);
        }
      });
      setKeys(set);
    } catch (err) {
      console.error('Failed to fetch alerted trades:', err);
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, [user]);

  useEffect(() => {
    fetchAlerted();
  }, [fetchAlerted]);

  // Refresh on tab focus (defer realtime for v1)
  useEffect(() => {
    if (!user) return;
    const onFocus = () => { fetchAlerted(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [user, fetchAlerted]);

  const wasAlerted = useCallback(
    (tradeId: string, source: AlertedTradeSource) => {
      if (!user || !tradeId) return false;
      return keys.has(`${source}:${tradeId}`);
    },
    [keys, user]
  );

  return (
    <AlertedTradesContext.Provider
      value={{ wasAlerted, count: keys.size, loading, refresh: fetchAlerted }}
    >
      {children}
    </AlertedTradesContext.Provider>
  );
};

export const useAlertedTrades = () => {
  const ctx = useContext(AlertedTradesContext);
  if (!ctx) {
    throw new Error('useAlertedTrades must be used within AlertedTradesProvider');
  }
  return ctx;
};
